import { Component, OnInit, Optional } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { firstValueFrom } from 'rxjs';

import { CatalogService } from '../../../services/catalog.service';
import { Catalog } from '@nousmedis/cc-types';
import { AuthModel } from '@nousmedis/nm-guard-auth';
import { User } from '@nousmedis/guard-types/dist/frontend';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { catalogFormFields } from '../../../schemas/catalog-form.schema';

@Component({
  selector: 'app-catalog-form',
  templateUrl: './catalog-form.component.html',
  styleUrls: ['./catalog-form.component.scss'],
})
export class CatalogFormComponent implements OnInit {
  form = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];

  loadedForm = false;
  connStatus = false;
  connStatusProgress = false;
  connTest = false;
  testConnectionShow = false;
  testConnectionIcon = '';
  testConnectionMessage = '';
  connectorSync = '';
  privateKeySync = '';
  idCatalog = '';
  isNew = true;
  user: User;

  constructor(
    private catalogService: CatalogService,
    private auth: AuthModel,
    private messageService: MessageService,
    private translate: TranslateService,
    @Optional() private dialogConfig?: DynamicDialogConfig,
    @Optional() private dialogRef?: DynamicDialogRef,
  ) {
    this.user = this.auth.getCurrentUser();
  }

  ngOnInit(): void {
    this.fields = catalogFormFields(this.translate);

    if (this.dialogConfig?.data?.id) {
      this.idCatalog = this.dialogConfig.data.id;
      this.isNew = false;
    }

    if (this.idCatalog) {
      this.loadCatalogInForm();
    } else {
      this.model = {
        createdBy: this.user.id,
        updatedBy: this.user.id,
        extraFields: [],
      };
      this.loadedForm = true;
      this.watchConnectionFields();
    }

    this.dialogRef?.onClose.subscribe((data) => {
      if (this.isNew && !data) {
        this.catalogService.delete([this.idCatalog]);
      }
    });
  }

  private watchConnectionFields(): void {
    this.form.statusChanges.subscribe(() => {
      this.testConnectionShow = this.form.valid;
    });
    this.form.get('connector')?.valueChanges.subscribe(() => {
      this.connStatus = false;
    });
    this.form.get('privateKey')?.valueChanges.subscribe(() => {
      this.connStatus = false;
    });
  }

  private loadCatalogInForm(): void {
    this.loadedForm = false;
    this.catalogService.get(this.idCatalog).subscribe((catalog: Catalog) => {
      this.model = {
        id: catalog.id,
        name: catalog.name,
        title: catalog.title,
        connector: '',
        privateKey: '',
        createdBy: catalog.createdBy,
        updatedBy: catalog.updatedBy,
        createdAt: catalog.createdAt,
        updatedAt: catalog.updatedAt,
        extraFields: (catalog.extra || [])
          .filter((e) => e !== null)
          .map((e) => ({ key: e.label || e.name, value: e.value })),
      };
      this.loadedForm = true;
      this.connStatus = true;
      this.connTest = true;
      this.testConnectionShow = this.form.valid;
      this.watchConnectionFields();
    });
  }

  private buildCatalogModel(): Catalog {
    const catalog = this.catalogService.initializeCatalog();
    catalog.id = this.model.id;
    catalog.name = this.model.name;
    catalog.title = this.model.title;
    catalog.createdBy = this.model.createdBy;
    catalog.updatedBy = this.model.updatedBy;
    if (this.model.createdAt) catalog.createdAt = this.model.createdAt;
    if (this.model.updatedAt) catalog.updatedAt = this.model.updatedAt;
    catalog.extra = (this.model.extraFields || []).map(
      (ef: { key: string; value: string }) => ({
        name: ef.key.replace(/ /g, '_').toLowerCase(),
        label: ef.key,
        value: ef.value,
      })
    ) as any;
    return catalog;
  }

  testConnection(): void {
    firstValueFrom(this.catalogService.save(this.buildCatalogModel())).then((savedForm: any) => {
      this.idCatalog = savedForm.id;
      this.model = { ...this.model, id: savedForm.id };
      this.connectorSync = this.model.connector;
      this.privateKeySync = this.model.privateKey;
      this.connStatus = false;
      this.connStatusProgress = true;
    }).catch(() => {
      this.messageService.add({
        key: 'root-msg',
        summary: 'Catalog',
        detail: 'No se puede testear la conexión por un error en el catalogo',
        severity: 'error',
      });
    });
  }

  saveCatalog(close = true): void {
    if (!this.connTest) {
      this.messageService.add({
        key: 'root-msg',
        summary: 'Catalog',
        detail: 'the Key or Private key is incorrect',
        severity: 'error',
      });
      return;
    }
    this.catalogService.save(this.buildCatalogModel()).subscribe((data: any) => {
      this.messageService.add({
        key: 'root-msg',
        summary: 'Catalog',
        detail: 'Catalog saved successfully',
        severity: 'success',
      });
      if (close) {
        this.dialogRef?.close(data);
      } else {
        this.idCatalog = data.id;
      }
    });
  }

  setConnectionStatus(event: boolean): void {
    this.connStatus = event;
  }
}
